import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { IExternalContacts, IRefRegisterProfileData, IRegisterProfileData } from 'entities';
import { Controller, useForm } from 'react-hook-form';
import { Button, IconButton, Popover } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { toPattern } from 'vanilla-masker';
import { useLocation } from 'react-router-dom';
import { IFileViewModel } from 'viewModels';
import ErrorIcon from '@mui/icons-material/Error';
import { ReactComponent as WarningIcon } from '../../assets/icons/warning-icon.svg';
import { Container, Content, ContentSummary, PopoverContainer } from './styles';
import { Accordion } from '../Accordion';
import { DropZone } from '../DropZone';
import { sendCodeEmail } from '../../controller/EmailController';
import {
  RegistrationOrigin,
  TypeContact,
  TypeUser,
  labelExternalContacts,
} from '../../types/enums';
import { Input } from '../Inputs/Input';
import { ReactComponent as CheckCircle } from '../../assets/icons/IconCheckCircle.svg';
import { ReactComponent as CloseCircle } from '../../assets/icons/IconCloseCircle.svg';
import { validateEmail } from '../../utils/validateEmail';
import { validatePhone } from '../../utils/validatePhone';
import { unMask } from '../../utils/removeMask';
import {
  normalizeTypeContact,
  sanitizeContactValueForType,
} from '../../utils/externalContactType';
import { SendVerificationModal } from '../Modals/ModalSendEmail';
import { UserController } from '../../controller/UserController';
import { useApp, useAuth, useRegisterForm } from '../../context';
import { slugify } from '../../utils/slugify';
import { MASK_PHONE } from '../../constants';
import { decryptCode } from '../../utils/crypto';
import { useAlert } from '../../hooks/useAlert';
import { CircularProgressWithLabel } from '../CircularProgressWithLabel';
import { useConfig } from '../../context/configCustom';
import { DropDownList } from '../DropDownList';

export const RegisterProfile = forwardRef<IRefRegisterProfileData>((_, ref) => {
  const refComponent = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const app = useApp();
  const alert = useAlert();
  const { pathResourceEmail } = useConfig();
  const color =
    location.pathname === '/naturalPersonAccount' || location.pathname === '/legalPersonAccount'
      ? 'background'
      : 'background_white';
  const LIMIT_EXTERNAL_CONTACT = 3;
  const LIMIT_ALL_CONTACTS = Object.keys(labelExternalContacts).length * LIMIT_EXTERNAL_CONTACT;
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [expectedCode, setExpectedCode] = useState('');
  const [displaySummary, setDisplaySummary] = useState(false);
  const [isOpenValidadeContactModal, setIsOpenValidadeContactModal] = useState(false);
  const [isOpenPopoverExternalContact, setIsOpenPopoverExternalContact] = useState(false);
  const [currentExternalContacts, setCurrentExternalContacts] = useState<IExternalContacts>(
    {} as IExternalContacts,
  );
  const [pictureProfileField, setPictureProfileField] = useState<IFileViewModel | undefined>(
    undefined,
  );
  const [externalContactsField, setExternalContactsField] = useState<IExternalContacts[]>([]);
  const { user } = useAuth();
  const {
    activeForm,
    setActiveForm,
    pictureProfile,
    externalContacts,
    linkFacebook,
    linkInstagram,
    linkLinkedIn,
    linkTwitter,
    linkSite,
    presentationLetter,
    registrationOrigin,
    formSubmited,
  } = useRegisterForm();

  const defaultValues = {
    externalContacts: [
      {
        id: uuid(),
        value: '',
        isVerified: false,
        isValid: false,
        isEmpty: true,
        type: TypeContact.EMAIL,
      },
    ],
    facebook: linkFacebook,
    instagram: linkInstagram,
    linkedin: linkLinkedIn,
    twitter: linkTwitter,
    site: linkSite,
    presentationLetter,
    registrationOrigin,
  };

  useEffect(() => {
    if (activeForm === 'registerProfile') {
      setDisplaySummary(false);
    } else {
      setDisplaySummary(true);
    }
  }, [activeForm]);

  const handleHiddenSummary = () => {
    setDisplaySummary(!displaySummary);
    setActiveForm('registerProfile');
  };
  const {
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const data = watch();

  const handleAddExternalContact = (type: TypeContact) => {
    setIsOpenPopoverExternalContact(false);
    const externalContactsFiltered = externalContactsField.filter(
      (externalContact) => externalContact.type === type,
    );
    if (externalContactsFiltered.length < LIMIT_EXTERNAL_CONTACT) {
      setExternalContactsField((prevState) => [
        ...prevState,
        {
          id: uuid(),
          value: '',
          isVerified: false,
          isValid: false,
          isEmpty: true,
          alreadySentConfirmation: false,
          alreadyExists: false,
          type,
        },
      ]);
    }
  };

  const handleRemoveExternalContact = (id: string) => {
    const idx = externalContactsField.findIndex((item) => item.id === id);
    setExternalContactsField((prevState) => prevState.filter((item) => item.id !== id));
    if (idx > -1) {
      setExternalContactsField((prevState) => prevState.filter((item) => item.id !== id));
    }
  };
  const handleOpenPopoverExternalContact = () => {
    setIsOpenPopoverExternalContact(true);
  };
  const handleClosePopoverExternalContact = () => {
    setIsOpenPopoverExternalContact(false);
  };

  const handleValidateExternalContacts = (
    value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    type: TypeContact,
  ) => {
    const contactType = normalizeTypeContact(type);
    const inputValue = value.target.value;
    const normalizedValue = sanitizeContactValueForType(contactType, inputValue);

    const isRepeated = externalContactsField
      .filter((c) => normalizeTypeContact(c.type) === contactType)
      .some((c) => {
        const existingValue = sanitizeContactValueForType(
          contactType,
          c.value ?? '',
        );
        return existingValue === normalizedValue && c.id !== externalContactsField[index].id;
      });

    const validator = contactType === TypeContact.EMAIL ? validateEmail : validatePhone;

    setExternalContactsField((prevState) => {
      const newState = [...prevState];
      newState[index].type = contactType;
      newState[index].isValid = !!normalizedValue && validator(normalizedValue);
      newState[index].alreadyExists = isRepeated;
      newState[index].isEmpty = !normalizedValue;
      newState[index].value =
        contactType === TypeContact.EMAIL ? inputValue.trim() : normalizedValue;
      newState[index].isVerified = false;
      return newState;
    });
  };
  const handleOpenModelValidadeContact = (externalContactsSelected: IExternalContacts) => {
    if (!pathResourceEmail) {
      alert.show({
        type: 'error',
        message: 'Não foi possível enviar o e-mail, tente novamente!',
      });
      return;
    }
    const selectedType = normalizeTypeContact(externalContactsSelected.type);

    if (!externalContactsSelected.alreadySentConfirmation) {
      if (selectedType === TypeContact.EMAIL) {
        app.setLoading(true);
        sendCodeEmail({
          to: externalContactsSelected.value,
          type: 'activeContact',
          pathResource: pathResourceEmail,
        })
          .then((resCode) => {
            setExpectedCode(decryptCode(resCode));
            setExternalContactsField((prevState) => {
              const newState = [...prevState];
              const index = newState.findIndex((item) => item.id === externalContactsSelected.id);
              if (index > -1) {
                newState[index].alreadySentConfirmation = true;
                return newState;
              }
              return prevState;
            });
          })
          .catch(() => {
            alert.show({
              type: 'error',
              message: 'Não foi possível enviar o e-mail, tente novamente!',
            });
          })
          .finally(() => {
            app.setLoading(false);
          });
      }
      if (selectedType === TypeContact.WHATSAPP) {
        UserController.sendWhatsappConfirmation(`55${unMask(externalContactsSelected.value)}`).then(
          (resCode) => {
            setExpectedCode(decryptCode(resCode));
            setExternalContactsField((prevState) => {
              const newState = [...prevState];
              const index = newState.findIndex((item) => item.id === externalContactsSelected.id);
              if (index > -1) {
                newState[index].alreadySentConfirmation = true;
                return newState;
              }
              return prevState;
            });
          },
        );
      }
    }
    if (externalContactsSelected.isValid) {
      setCurrentExternalContacts(externalContactsSelected);
      setIsOpenValidadeContactModal(true);
    }
  };

  const handleVerifyExternalContact = (newExternalContact: IExternalContacts) => {
    setExternalContactsField((prevState) => {
      const newState = [...prevState];
      const index = newState.findIndex((item) => item.id === newExternalContact.id);
      if (index > -1) {
        newState[index].isVerified = true;
      } else {
        newState.push(newExternalContact);
      }
      return newState;
    });
    setIsOpenValidadeContactModal(false);
  };

  const handleUpdateProfileData = async () => {
    const newData: IRegisterProfileData = {
      hasError: await checkHasError(),
      pictureProfile: pictureProfileField,
      externalContacts: externalContactsField,
      presentationLetter: data.presentationLetter,
      registrationOrigin: data.registrationOrigin,
      linkFacebook: slugify(data.facebook),
      linkInstagram: slugify(data.instagram),
      linkLinkedIn: slugify(data.linkedin),
      linkTwitter: slugify(data.twitter),
      linkSite: slugify(data.site),
    };
    return newData;
  };

  const handleFocusOnError = () => {
    refComponent.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkHasError = useCallback(async () => {
    let hasError = false;

    if (data.registrationOrigin === undefined) {
      setError('registrationOrigin', { type: 'manual', message: 'Campo obrigatório' });
      hasError = true;
    } else {
      clearErrors('registrationOrigin');
    }

    return hasError;
  }, [clearErrors, data.registrationOrigin, setError]);

  useImperativeHandle(ref, () => ({
    handleUpdateProfileData,
    handleFocusOnError,
  }));

  useEffect(() => {
    checkHasError();
  }, [checkHasError]);

  useEffect(() => {
    if (externalContacts.length > 0) {
      externalContacts.forEach((externalContact) => {
        setValue(externalContact.id as any, externalContact.value);
      });
    }
    setPictureProfileField(pictureProfile);
    setExternalContactsField(externalContacts);
    setValue('presentationLetter', presentationLetter);
    setValue('facebook', linkFacebook);
    setValue('instagram', linkInstagram);
    setValue('linkedin', linkLinkedIn);
    setValue('twitter', linkTwitter);
    setValue('site', linkSite);
  }, [
    externalContacts,
    linkFacebook,
    linkInstagram,
    linkLinkedIn,
    linkSite,
    linkTwitter,
    pictureProfile,
    presentationLetter,
    setValue,
  ]);

  return (
    <Container color={color} id="contacts">
      {!displaySummary && (
        <Content>
          <div className="containerPicture">
            <Accordion title="Foto de Perfil" isOpen type="default">
              <div className="contentPicture">
                <div className="containerDropzone">
                  <DropZone
                    type="picture"
                    multipleFiles={false}
                    onFile={({ file }) => {
                      setPictureProfileField({
                        file,
                        id: uuid(),
                      });
                    }}
                    renderSummary
                    onRemoveFile={() => setPictureProfileField(undefined)}
                    defaultValues={pictureProfileField?.file}
                    uploadHandler={UserController.updateProfilePicture}
                    fileId={pictureProfileField?.id}
                  />
                </div>
              </div>
            </Accordion>
            <div className="icon">
              <IconButton onClick={handleHiddenSummary}>
                <ExpandCircleDownIcon
                  fontSize="large"
                  color={
                    data.facebook ||
                    data.instagram ||
                    data.linkedin ||
                    data.twitter ||
                    data.site ||
                    data.presentationLetter ||
                    externalContactsField.length ||
                    pictureProfileField
                      ? 'success'
                      : 'error'
                  }
                  className="icon"
                />
              </IconButton>
            </div>
          </div>
          <div className="containerContacts">
            <Accordion title="Contatos" isOpen type="default">
              {externalContactsField.map((externalContact, index) => {
                const contactType = normalizeTypeContact(externalContact.type);
                return (
                <div key={externalContact.id} className="contentContacts">
                  <div className="contact">
                    <p>{labelExternalContacts[contactType]}</p>
                    <Input
                      fullWidth
                      name={externalContact.id}
                      variant="standard"
                      control={control}
                      value={externalContact.value ?? ''}
                      onChange={(event) =>
                        handleValidateExternalContacts(event, index, contactType)
                      }
                      placeholder={
                        contactType === TypeContact.EMAIL ? 'E-mail' : '(xx) xxxxx-xxxx'
                      }
                      mask={contactType === TypeContact.EMAIL ? '' : MASK_PHONE}
                      preventNotNumbers={contactType !== TypeContact.EMAIL}
                      lengthInput={contactType === TypeContact.EMAIL ? 100 : 15}
                      errorText={
                        !externalContact.isValid && !externalContact.isEmpty
                          ? `Contato inválido`
                          : externalContact.alreadyExists
                          ? 'Contato ja cadastrado'
                          : ''
                      }
                    />
                  </div>
                  <div className="containerButton">
                    <div className="containerRemoveContact">
                      {!!index && (
                        <Button
                          type="button"
                          onClick={() => handleRemoveExternalContact(externalContact.id)}
                          className="removeContact">
                          <CloseCircle />{' '}
                        </Button>
                      )}
                    </div>
                    {externalContact.isValid &&
                      !externalContact.isEmpty &&
                      !externalContact.isVerified && (
                        <Button
                          type="button"
                          className="validade"
                          onClick={() => handleOpenModelValidadeContact(externalContact)}>
                          Validar
                        </Button>
                      )}
                    {externalContact.isVerified && <CheckCircle />}
                  </div>
                </div>
              );
              })}
              {externalContactsField.length !== LIMIT_ALL_CONTACTS ? (
                <div className="containerAddContact">
                  <Button
                    type="button"
                    onClick={handleOpenPopoverExternalContact}
                    ref={anchorRef}
                    className="addContact">
                    Adicionar Contato <AddCircleOutlineIcon />
                  </Button>
                  <Popover
                    open={isOpenPopoverExternalContact}
                    onClose={handleClosePopoverExternalContact}
                    anchorEl={anchorRef.current}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}>
                    <PopoverContainer>
                      {Object.keys(labelExternalContacts).map((contactType) => {
                        const type = normalizeTypeContact(Number(contactType));
                        const externalContactsFiltered = externalContactsField.filter(
                          (externalContact) =>
                            normalizeTypeContact(externalContact.type) === type,
                        );
                        if (externalContactsFiltered.length === LIMIT_EXTERNAL_CONTACT)
                          return undefined;
                        return (
                          <button
                            type="button"
                            key={contactType}
                            onClick={() => handleAddExternalContact(type)}>
                            {labelExternalContacts[type]}
                          </button>
                        );
                      })}
                    </PopoverContainer>
                  </Popover>
                </div>
              ) : (
                <div className="exceededContacts">
                  <WarningIcon />
                  <p className="limitContacts">Limite de contatos atingido</p>
                </div>
              )}
            </Accordion>
          </div>
          <div>
            <Accordion
              title={
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '1rem',
                  }}>
                  <span>Carta de apresentação</span>
                </div>
              }
              isOpen
              type="default">
              <div className="contentPresentationLetter">
                <Controller
                  control={control}
                  name="presentationLetter"
                  render={({ field: { value, onChange, ...rest } }) => (
                    <>
                      <textarea
                        className="presentationLetter"
                        placeholder="Escreva aqui um pouco da sua experiência, para quando alguém entrar no seu perfil saber um pouco mais sobre você"
                        value={value}
                        onChange={onChange}
                        maxLength={500}
                        {...rest}
                      />
                      {!!data.presentationLetter.length && (
                        <div className="progress">
                          <CircularProgressWithLabel
                            variant="determinate"
                            size={35}
                            color={
                              (data.presentationLetter.length / 500) * 100 <= 85
                                ? 'success'
                                : (data.presentationLetter.length / 500) * 100 < 100
                                ? 'info'
                                : 'primary'
                            }
                            value={(data.presentationLetter.length / 500) * 100}
                          />
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </Accordion>
          </div>
          <div className="containerSocial">
            <Accordion title="Redes Sociais" isOpen type="default">
              <div className="contentSocial">
                <div className="social">
                  <p>facebook/</p>
                  <Input control={control} name="facebook" lengthInput={80} />
                </div>
                <div className="social">
                  <p>instagram/</p>
                  <Input control={control} name="instagram" lengthInput={80} />
                </div>
                <div className="social">
                  <p>Linkedin/</p>
                  <Input control={control} name="linkedin" lengthInput={80} />
                </div>
                <div className="social">
                  <p>Seu site</p>
                  <Input control={control} name="site" lengthInput={80} />
                </div>
              </div>
            </Accordion>
          </div>
          {user.type === TypeUser.BASEUSER && (
            <div className="containerOrigin">
              <p>Como conheceu a nossa plataforma?</p>
              <div className="selectOrigin">
                <Controller
                  control={control}
                  name="registrationOrigin"
                  render={({ field: { onChange } }) => (
                    <DropDownList
                      placeholderTitle="Selecione"
                      itens={[
                        { id: RegistrationOrigin.INSTAGRAM, value: 'Instragram' },
                        { id: RegistrationOrigin.FACEBOOK, value: 'Facebook' },
                        { id: RegistrationOrigin.LINKEDIN, value: 'LinkedIn' },
                        { id: RegistrationOrigin.EMAIL, value: 'E-mail' },
                        { id: RegistrationOrigin.INDICATION, value: 'Indicação' },
                        { id: RegistrationOrigin.OTHER, value: 'Outro' },
                      ]}
                      typeReturn="id"
                      onChange={onChange}
                      width="10rem"
                    />
                  )}
                />
                {formSubmited && errors?.registrationOrigin && (
                  <span className="fieldError">
                    <ErrorIcon fontSize="inherit" /> {errors.registrationOrigin?.message}
                  </span>
                )}
              </div>
            </div>
          )}
        </Content>
      )}
      {displaySummary && (
        <ContentSummary>
          <div className="containerSummary">
            <div className="containerPicture">
              <div className="headerSummary">
                <button type="button" onClick={handleHiddenSummary}>
                  Foto de Perfil
                </button>
                <IconButton onClick={handleHiddenSummary}>
                  <ExpandCircleDownIcon
                    fontSize="large"
                    color={
                      data.facebook ||
                      data.instagram ||
                      data.linkedin ||
                      data.twitter ||
                      data.site ||
                      data.presentationLetter ||
                      externalContactsField.length > 0 ||
                      pictureProfileField
                        ? 'success'
                        : 'error'
                    }
                    className="icon"
                  />
                </IconButton>
              </div>
              {pictureProfileField?.file && (
                <img src={URL.createObjectURL(pictureProfileField.file)} alt="Foto de Perfil" />
              )}
            </div>
            <div className="containerContacts">
              <button type="button" onClick={handleHiddenSummary}>
                Contatos
              </button>
              <div className="contentContacts">
                {externalContactsField
                  .filter((ex) => ex.isVerified)
                  .map((externalContact) => (
                    <div key={externalContact.id} className="contact">
                      <p>{labelExternalContacts[externalContact.type]}</p>
                      <button type="button" onClick={handleHiddenSummary}>
                        {toPattern(
                          externalContact.value,
                          externalContact.type === TypeContact.EMAIL
                            ? externalContact.value
                            : MASK_PHONE,
                        )}
                      </button>
                      {externalContact.isVerified ? <CheckCircle /> : ''}
                    </div>
                  ))}
              </div>
            </div>
            <div className="summaryItens">
              <button onClick={handleHiddenSummary} type="button">
                Carta de apresentação
              </button>
              {data.presentationLetter && (
                <div className="contentPresentationLetter">
                  <button onClick={handleHiddenSummary} type="button">
                    {data.presentationLetter}
                  </button>
                </div>
              )}
            </div>
            <div className="containerSocial">
              <button type="button" onClick={handleHiddenSummary}>
                Redes Sociais
              </button>
              <div className="contentSocial">
                <div className="social">
                  <button type="button" onClick={handleHiddenSummary}>
                    facebook/
                  </button>
                  <button type="button" onClick={handleHiddenSummary}>
                    {data.facebook}
                  </button>
                </div>
                <div className="social">
                  <button type="button" onClick={handleHiddenSummary}>
                    instagram/
                  </button>
                  <button type="button" onClick={handleHiddenSummary}>
                    {data.instagram}
                  </button>
                </div>
                <div className="social">
                  <button type="button" onClick={handleHiddenSummary}>
                    Linkedin/
                  </button>
                  <button type="button" onClick={handleHiddenSummary}>
                    {data.linkedin}
                  </button>
                </div>
                <div className="social">
                  <button type="button" onClick={handleHiddenSummary}>
                    Seu site
                  </button>
                  <button type="button" onClick={handleHiddenSummary}>
                    {data.site}
                  </button>
                </div>
              </div>
            </div>
            {user.type === TypeUser.BASEUSER && (
              <div className="containerOrigin">
                <p>Como conheceu a nossa plataforma?</p>
                <div className="selectOrigin">
                  <Controller
                    control={control}
                    name="registrationOrigin"
                    render={({ field: { onChange } }) => (
                      <DropDownList
                        placeholderTitle="Selecione"
                        itens={[
                          { id: RegistrationOrigin.INSTAGRAM, value: 'Instragram' },
                          { id: RegistrationOrigin.FACEBOOK, value: 'Facebook' },
                          { id: RegistrationOrigin.LINKEDIN, value: 'LinkedIn' },
                          { id: RegistrationOrigin.EMAIL, value: 'E-mail' },
                          { id: RegistrationOrigin.INDICATION, value: 'Indicação' },
                          { id: RegistrationOrigin.OTHER, value: 'Outro' },
                        ]}
                        typeReturn="id"
                        onChange={onChange}
                        width="10rem"
                      />
                    )}
                  />
                  {formSubmited && errors?.registrationOrigin && (
                    <span className="fieldError">
                      <ErrorIcon fontSize="inherit" /> {errors.registrationOrigin?.message}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </ContentSummary>
      )}
      <SendVerificationModal
        isOpen={isOpenValidadeContactModal}
        onRequestClose={() => {
          setIsOpenValidadeContactModal(false);
        }}
        onCodeChange={setExpectedCode}
        data={{
          codeExpected: expectedCode,
          contactLabel: labelExternalContacts[currentExternalContacts.type],
          contactValue: currentExternalContacts.value,
        }}
        onCallback={() => handleVerifyExternalContact(currentExternalContacts)}
      />
    </Container>
  );
});
